// ProviderStatus — admin-only Razorpay payment-ecosystem health board.
//
// Route: /admin/provider-status, gated by RequireAuth role="admin".
// Backed by GET /v1/admin/razorpay/downtimes (server-side proxy to
// api.razorpay.com — credentials never reach the browser).
//
// Polls every 60s. Razorpay's published cadence for downtime data is
// "best-effort within ~5 min" so a tighter poll wouldn't surface anything
// the server-side cache (also 60s) hasn't already seen.
//
// Visual model: matches the four-column layout from Razorpay's own
// "Ecosystem Health" view — UPI, Cards, Net Banking, E-mandate — with
// per-column status banners and per-instrument row cards (logo + name
// + status dot). Affected instruments render with a warn-tinted card
// background + a red status dot; healthy instruments stay on the
// surface colour with a green dot. See components/ProviderStatus/.
//
// not_configured (HTTP 503): the operator hasn't set RAZORPAY_KEY /
// RAZORPAY_SECRET on the backend. Renders an empty state with the env
// var names so the next operator knows what's missing.
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { I } from "@/components/Icons";
import { EcosystemColumn } from "@/components/ProviderStatus/EcosystemColumn";
import {
  listRazorpayDowntimes,
  type RazorpayDowntime,
} from "@/api/razorpayDowntimes";
import { COLUMNS } from "@/data/razorpayEcosystem";
import { extractErrorCode } from "@/utils/apiError";
import { formatDateTime } from "@/utils/format";

export function ProviderStatus() {
  const { data, isLoading, isError, error, refetch, isFetching, dataUpdatedAt } =
    useQuery({
      queryKey: ["razorpay-downtimes"],
      queryFn: () => listRazorpayDowntimes(),
      refetchInterval: 60_000,
      staleTime: 30_000,
    });

  const downtimes = useMemo(() => data ?? [], [data]);
  const notConfigured = isError && extractErrorCode(error) === "not_configured";

  // Partition downtimes across the 4 method columns. Each downtime can
  // belong to at most one column (the match function returns null when
  // the downtime isn't this column's responsibility).
  const byColumn = useMemo(() => {
    const buckets: Record<string, RazorpayDowntime[]> = {};
    for (const col of COLUMNS) {
      buckets[col.key] = [];
    }
    for (const d of downtimes) {
      for (const col of COLUMNS) {
        if (col.methods.includes(d.method)) {
          buckets[col.key].push(d);
          break;
        }
      }
    }
    return buckets;
  }, [downtimes]);

  // Top headline banner. Mirrors Razorpay's wording: "Few drops noticed in
  // Cards" when the only active downtimes are in one column; "Multiple
  // ecosystems affected" when they span two or more; "All systems
  // operational" when nothing is ongoing.
  const headline = useMemo(() => buildHeadline(byColumn), [byColumn]);

  return (
    <div className="page">
      <div
        className="row"
        style={{
          alignItems: "baseline",
          justifyContent: "space-between",
          marginBottom: 16,
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div>
          <h1 style={{ margin: 0, letterSpacing: "-0.01em" }}>
            Ecosystem Health
          </h1>
          <div className="dim" style={{ fontSize: 12, marginTop: 4 }}>
            Razorpay payment-rail status across UPI, cards, netbanking and
            e-mandate. Polled every 60 seconds.
          </div>
        </div>
        <div className="row gap-8" style={{ alignItems: "center" }}>
          {dataUpdatedAt > 0 && (
            <span className="dim" style={{ fontSize: 11 }}>
              Last updated at{" "}
              {formatDateTime(new Date(dataUpdatedAt).toISOString())}
            </span>
          )}
          <button
            type="button"
            className="btn ghost sm"
            onClick={() => refetch()}
            disabled={isFetching}
            title="Force a refresh — bypasses the 60s polling cycle"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <I.Refresh
              size={11}
              style={{
                animation: isFetching ? "spin 800ms linear infinite" : undefined,
              }}
            />
            {isFetching ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {notConfigured ? (
        <EmptyCard>
          <h3 style={{ margin: 0 }}>Razorpay not configured</h3>
          <p style={{ marginTop: 8, marginBottom: 0 }}>
            The Razorpay credentials aren't set on the backend. Add{" "}
            <code>RAZORPAY_KEY</code> and <code>RAZORPAY_SECRET</code> to{" "}
            <code>/opt/strata/.env</code> and restart{" "}
            <code>payouts-api</code> — the page will start populating
            within 60 seconds.
          </p>
        </EmptyCard>
      ) : isError ? (
        <EmptyCard>
          <h3 style={{ margin: 0 }}>Couldn't reach Razorpay</h3>
          <p style={{ marginTop: 8, marginBottom: 0 }}>
            The server-side proxy returned an error. This is usually a
            transient network blip; the next poll will retry. Force a
            refresh to try sooner.
          </p>
        </EmptyCard>
      ) : (
        <>
          {/* Top headline banner — affects vs all-clear. */}
          <HeadlineBanner headline={headline} loading={isLoading} />

          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit, minmax(260px, 1fr))",
              gap: 20,
              marginTop: 16,
              alignItems: "flex-start",
            }}
          >
            {COLUMNS.map((col) => (
              <EcosystemColumn
                key={col.key}
                column={col}
                downtimes={byColumn[col.key] ?? []}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Top headline banner ───────────────────────────────────────────────────

interface Headline {
  tone: "ok" | "amber" | "warn";
  text: string;
}

function buildHeadline(byColumn: Record<string, RazorpayDowntime[]>): Headline {
  const affected = Object.entries(byColumn).filter(([, v]) => v.length > 0);
  if (affected.length === 0) {
    return { tone: "ok", text: "All payment ecosystems are healthy" };
  }
  // Pick the worst severity across affected columns.
  const anyHigh = affected.some(([, list]) =>
    list.some((d) => d.severity === "high"),
  );
  const tone: Headline["tone"] = anyHigh ? "warn" : "amber";

  if (affected.length === 1) {
    const [key, list] = affected[0];
    const col = COLUMNS.find((c) => c.key === key);
    const label = col?.label ?? key;
    if (list.length === 1) {
      return { tone, text: `1 ongoing downtime in ${label}` };
    }
    return { tone, text: `${list.length} ongoing downtimes in ${label}` };
  }
  const total = affected.reduce((n, [, list]) => n + list.length, 0);
  return {
    tone,
    text: `${total} ongoing downtimes across ${affected.length} ecosystems`,
  };
}

function HeadlineBanner({
  headline,
  loading,
}: {
  headline: Headline;
  loading: boolean;
}) {
  const bg =
    headline.tone === "warn"
      ? "var(--warn-soft, #fef3f2)"
      : headline.tone === "amber"
        ? "var(--amber-soft, #fef9e7)"
        : "var(--ok-soft, #f0fdf4)";
  const border =
    headline.tone === "warn"
      ? "var(--warn, #b42318)"
      : headline.tone === "amber"
        ? "var(--amber, #b88a00)"
        : "var(--ok, #1a7f37)";
  const dotColor = border;
  return (
    <div
      style={{
        padding: "12px 16px",
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: 8,
        display: "flex",
        alignItems: "center",
        gap: 10,
        fontSize: 14,
        fontWeight: 500,
        opacity: loading ? 0.6 : 1,
      }}
    >
      <span
        style={{
          display: "inline-block",
          width: 10,
          height: 10,
          borderRadius: "50%",
          background: dotColor,
          flexShrink: 0,
        }}
      />
      <span>{loading ? "Checking ecosystem health…" : headline.text}</span>
    </div>
  );
}

function EmptyCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="card">
      <div className="empty">{children}</div>
    </div>
  );
}
